import { sql } from "drizzle-orm";
import {
  index,
  integer,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { buyingOrders } from "./buying-order";
import { catalogItems } from "./catalog-item";
import { orderLines } from "./order-line";

export const procurementUnitStatusEnum = pgEnum("procurement_unit_status", [
  "CREATED",
  "IN_TRANSIT",
  "IN_INVENTORY",
  "SOLD",
  "LOST_IN_TRANSIT",
  "COUNT_ADJUSTMENT",
  "CUSTOMER_SUPPORT",
  "DAMAGED",
  "DAMAGED_RECOVERED",
  "CANCELLED",
]);

export const procurementCostSourceEnum = pgEnum("procurement_cost_source", [
  "LEGACY",
  "PURCHASE_PRICE",
  "MARKET_PRICE",
]);

export const procurementSupportReasonEnum = pgEnum("procurement_support_reason", [
  "RESHIPMENT",
  "VIP",
  "MARKETING",
  "OTHER",
]);

export const procurementDamageOriginEnum = pgEnum("procurement_damage_origin", [
  "IN_TRANSIT",
  "IN_INVENTORY",
  "RETURNED",
]);

/**
 * One physical card and its warehouse lifecycle.
 * Writer: platform staff, receiving, shipping, and aging jobs.
 */
export const procurements = pgTable(
  "procurements",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    catalogItemId: uuid("catalog_item_id")
      .notNull()
      .references(() => catalogItems.id),
    buyingOrderId: uuid("buying_order_id").references(() => buyingOrders.id),
    /** Buylist order-item id. Null on internal TCGPlayer units. */
    externalLineId: text("external_line_id"),
    /** 0-based slot within that buylist line. Null on internal units. */
    externalSlot: integer("external_slot"),
    status: procurementUnitStatusEnum("status").notNull(),
    unitCostCents: integer("unit_cost_cents").notNull(),
    costSource: procurementCostSourceEnum("cost_source").notNull(),
    certNumber: text("cert_number"),
    gradingCompany: text("grading_company"),
    grade: text("grade"),
    supportReason: procurementSupportReasonEnum("support_reason"),
    damageOrigin: procurementDamageOriginEnum("damage_origin"),
    expectedLossCents: integer("expected_loss_cents"),
    detail: text("detail"),
    soldOrderLineId: uuid("sold_order_line_id").references(() => orderLines.id),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    inTransitAt: timestamp("in_transit_at", { withTimezone: true }),
    inInventoryAt: timestamp("in_inventory_at", { withTimezone: true }),
    soldAt: timestamp("sold_at", { withTimezone: true }),
    resolvedAt: timestamp("resolved_at", { withTimezone: true }),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("procurements_catalog_status_idx").on(t.catalogItemId, t.status),
    index("procurements_buying_order_idx").on(t.buyingOrderId),
    index("procurements_status_in_transit_at_idx").on(t.status, t.inTransitAt),
    uniqueIndex("procurements_buying_external_slot_idx")
      .on(t.buyingOrderId, t.externalLineId, t.externalSlot)
      .where(
        sql`${t.buyingOrderId} is not null and ${t.externalLineId} is not null and ${t.externalSlot} is not null`,
      ),
    uniqueIndex("procurements_cert_idx")
      .on(t.gradingCompany, t.certNumber)
      .where(sql`${t.gradingCompany} is not null and ${t.certNumber} is not null`),
  ],
);
